// Lissandra Ice Tomb — frost ring expands, then 8 ice crystals erupt from the ground.
// Phases: RING (frost circle) → SPIKES (ice crystals erupt, staggered) → FADE

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const RING_MS  = 200;
const SPIKE_MS = 500;
const FADE_MS  = 350;
const TOTAL    = RING_MS + SPIKE_MS + FADE_MS;

const CRYSTAL_COUNT = 8;
const CRYSTALS = Array.from({ length: CRYSTAL_COUNT }, (_, i) => ({
  angle:  (i / CRYSTAL_COUNT) * Math.PI * 2 + 0.2,
  delay:  i * 0.1,     // stagger 0..0.7 of spike phase
  height: 28 + (i % 3) * 8,
  width:  5 + (i % 2) * 3,
  tilt:   (Math.random() * 0.3 - 0.15),
}));

export class LissandraFrostVFX implements Animation {
  private elapsed = 0;

  constructor(
    private center: Point,
    private radius: number,
    private onImpact?: () => void,
  ) {}

  update(dt: number): void {
    const prev = this.elapsed;
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    if (prev < RING_MS && this.elapsed >= RING_MS) this.onImpact?.();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const e = this.elapsed;
    if (e < RING_MS) {
      this.drawRing(ctx, e / RING_MS, 1);
    } else if (e < RING_MS + SPIKE_MS) {
      this.drawRing(ctx, 1, 1);
      this.drawSpikes(ctx, (e - RING_MS) / SPIKE_MS, 1);
    } else {
      const ft = (e - RING_MS - SPIKE_MS) / FADE_MS;
      this.drawRing(ctx, 1, 1 - ft);
      this.drawSpikes(ctx, 1, 1 - Easing.easeInQuad(ft));
    }
  }

  private drawRing(ctx: CanvasRenderingContext2D, progress: number, alpha: number): void {
    const r = this.radius * Easing.easeOutQuad(progress);
    ctx.save();

    // Cold fill
    ctx.globalAlpha = alpha * 0.12 * progress;
    ctx.fillStyle   = "#ccffff";
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, r, 0, Math.PI * 2);
    ctx.fill();

    // Main frost ring
    ctx.globalAlpha = alpha * (0.4 + 0.5 * progress);
    ctx.strokeStyle = "#88ddff";
    ctx.lineWidth   = 3;
    ctx.shadowColor = "#0088cc";
    ctx.shadowBlur  = 16;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, r, 0, Math.PI * 2);
    ctx.stroke();

    // Inner ring (ice pattern)
    ctx.globalAlpha = alpha * 0.35 * progress;
    ctx.strokeStyle = "#ccffff";
    ctx.lineWidth   = 1.5;
    ctx.shadowBlur  = 8;
    ctx.beginPath();
    ctx.arc(this.center.x, this.center.y, r * 0.5, 0, Math.PI * 2);
    ctx.stroke();

    // Frost hexagonal crosshairs
    if (progress > 0.5) {
      const crossFade = (progress - 0.5) / 0.5;
      ctx.globalAlpha = alpha * crossFade * 0.4;
      ctx.strokeStyle = "#aaeeff";
      ctx.lineWidth   = 1;
      ctx.shadowBlur  = 4;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(this.center.x, this.center.y);
        ctx.lineTo(this.center.x + Math.cos(a) * r, this.center.y + Math.sin(a) * r);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  private drawSpikes(ctx: CanvasRenderingContext2D, t: number, alpha: number): void {
    ctx.save();

    for (const crys of CRYSTALS) {
      const local = Math.max(0, (t - crys.delay) / (1 - crys.delay));
      if (local <= 0) continue;

      const riseT = Easing.easeOutBack(Math.min(local * 2.5, 1));
      const px = this.center.x + Math.cos(crys.angle) * this.radius * 0.75;
      const py = this.center.y + Math.sin(crys.angle) * this.radius * 0.75;
      const h  = crys.height * riseT;
      const w  = crys.width;

      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(crys.angle + Math.PI / 2 + crys.tilt); // point outward from center

      // Crystal body — tapered hexagonal prism
      const iceGrad = ctx.createLinearGradient(-w, 0, w, 0);
      iceGrad.addColorStop(0, "rgba(100,200,255,0.3)");
      iceGrad.addColorStop(0.35, "rgba(200,240,255,0.95)");
      iceGrad.addColorStop(0.65, "rgba(150,220,255,0.8)");
      iceGrad.addColorStop(1, "rgba(80,170,255,0.25)");

      ctx.globalAlpha = alpha;
      ctx.fillStyle   = iceGrad;
      ctx.shadowColor = "#44aaff";
      ctx.shadowBlur  = 14;

      ctx.beginPath();
      // Hexagonal crystal: base on ground, tip pointing away
      ctx.moveTo(0,  -h);             // tip
      ctx.lineTo(w * 0.55, -h * 0.7);
      ctx.lineTo(w,  -h * 0.35);
      ctx.lineTo(w * 0.7, 0);         // base
      ctx.lineTo(-w * 0.7, 0);
      ctx.lineTo(-w, -h * 0.35);
      ctx.lineTo(-w * 0.55, -h * 0.7);
      ctx.closePath();
      ctx.fill();

      // Inner highlight face
      ctx.globalAlpha = alpha * 0.7;
      ctx.fillStyle   = "rgba(230,250,255,0.5)";
      ctx.shadowBlur  = 0;
      ctx.beginPath();
      ctx.moveTo(-w * 0.15, -h);
      ctx.lineTo(w * 0.3, -h * 0.65);
      ctx.lineTo(w * 0.2, -h * 0.1);
      ctx.lineTo(-w * 0.2, -h * 0.1);
      ctx.closePath();
      ctx.fill();

      // Tip glint
      ctx.globalAlpha = alpha * 0.9;
      ctx.fillStyle   = "#ffffff";
      ctx.shadowColor = "#aaddff";
      ctx.shadowBlur  = 10;
      ctx.beginPath();
      ctx.arc(0, -h, 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
